import { useCallback, useEffect, useRef, useState } from 'react'
import {
  ChevronLeft,
  ChevronRight,
  Download,
  FileWarning,
  Loader2,
  ZoomIn,
  ZoomOut,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { attachmentsApi } from '@/api/attachmentsApi'
import {
  DEFAULT_ZOOM,
  PREVIEW_TIMEOUT_MS,
  PREVIEW_UNAVAILABLE_MESSAGE,
  canZoomIn,
  canZoomOut,
  clampPage,
  hasPageNavigation,
  isPdf,
  pageLabel,
  zoomIn,
  zoomOut,
} from '@/lib/attachmentPreview'
import { formatBytes } from '@/components/renderer/AttachmentPreview'

type State =
  | { kind: 'loading' }
  | { kind: 'unavailable' }
  | { kind: 'image'; url: string }
  | { kind: 'pdf'; url: string }

/**
 * Read an attachment without leaving the record it belongs to.
 *
 * Opens over the current page rather than navigating: the record stays mounted,
 * so closing returns the reader to the same tab and the same scroll position,
 * and nothing is written to their machine. Download is still here, and still
 * the same file the list hands over.
 *
 * ## Three ways this can fail, and one thing they all say
 *
 * A type the viewer cannot render, a file that is corrupt or truncated, and a
 * render that never finishes all resolve to `unavailable` and the same message.
 * A reader does not care which of the three it is; the useful thing to say is
 * identical, and Download still works in every one. What must never happen is
 * an error page, a blank panel, or a spinner with no end — so the load is
 * raced against `PREVIEW_TIMEOUT_MS`.
 *
 * ## Why pdf.js and not an iframe
 *
 * The browser's own PDF viewer cannot be asked for a page count or told to hide
 * its navigation on a one-page document, and a presigned URL in an `<iframe>`
 * is blocked by CSP on some deployments — the reason the older inline preview
 * refused PDFs outright. Rendering to a canvas answers both: no frame, and the
 * document object reports `numPages`.
 *
 * The library is imported dynamically so it lands in its own chunk. The desktop
 * bundle is already large and `src/router/index.tsx` imports every page
 * statically, so a static import here would put a PDF engine in front of every
 * user on first paint, including those who never open an attachment.
 */
export function AttachmentPreviewDialog({
  submissionId,
  fieldId,
  attachmentId,
  fileName,
  sizeBytes,
  open,
  onOpenChange,
}: {
  submissionId: string
  fieldId: string
  /** Which file, on a field that holds several. Omit for a single-file field. */
  attachmentId?: string
  fileName: string
  // No `mimeType`: the server's answer is the authority on what can be
  // rendered, and a caller's copy could disagree with the stored row.
  sizeBytes: number
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [state, setState] = useState<State>({ kind: 'loading' })
  const [zoom, setZoom] = useState(DEFAULT_ZOOM)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  // Ask for the URL only when a preview is actually opened. It is short-lived
  // and single-use, so a cached one would hand back a dead link.
  useEffect(() => {
    if (!open) return
    let cancelled = false
    setState({ kind: 'loading' })
    setZoom(DEFAULT_ZOOM)
    setPage(1)
    setTotalPages(1)

    // Nothing may leave the panel loading forever — see the note above.
    const timer = window.setTimeout(() => {
      if (!cancelled) setState({ kind: 'unavailable' })
    }, PREVIEW_TIMEOUT_MS)

    attachmentsApi
      .getPreview(submissionId, fieldId, attachmentId)
      .then((res) => {
        if (cancelled) return
        if (!res.previewable) {
          setState({ kind: 'unavailable' })
          return
        }
        setState(isPdf(res.mimeType) ? { kind: 'pdf', url: res.url } : { kind: 'image', url: res.url })
      })
      .catch(() => {
        // Including a 403: a refusal is the server's to make, and the reader is
        // told the same thing either way rather than shown a stack of jargon.
        if (!cancelled) setState({ kind: 'unavailable' })
      })
      .finally(() => window.clearTimeout(timer))

    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [open, submissionId, fieldId, attachmentId])

  /**
   * Browser Back closes the panel instead of leaving the record.
   *
   * Opening pushes a history entry, so Back pops it and lands here rather than
   * on whatever the reader was looking at before the record. Closing any other
   * way goes back once to consume that entry — otherwise Back after a manual
   * close would silently reopen the panel.
   */
  useEffect(() => {
    if (!open) return
    window.history.pushState({ attachmentPreview: true }, '')
    const onPop = () => onOpenChange(false)
    window.addEventListener('popstate', onPop)
    return () => {
      window.removeEventListener('popstate', onPop)
      if (window.history.state?.attachmentPreview) window.history.back()
    }
  }, [open, onOpenChange])

  const download = useCallback(async () => {
    // Goes through the same endpoint as the list's Download, so it is the same
    // file, the same filename and the same audit outcome by construction.
    const res = await attachmentsApi.getDownloadUrl(submissionId, fieldId, attachmentId)
    window.open(res.url, '_blank', 'noopener,noreferrer')
  }, [submissionId, fieldId, attachmentId])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='flex h-[85vh] max-w-5xl flex-col gap-0 p-0'>
        <div className='flex shrink-0 flex-wrap items-center gap-2 border-b px-4 py-3 pr-12'>
          <div className='min-w-0 flex-1'>
            <DialogTitle className='truncate text-sm font-semibold' title={fileName}>
              {fileName}
            </DialogTitle>
            <DialogDescription className='text-xs'>
              {formatBytes(sizeBytes)}
              {state.kind === 'pdf' && <> · {pageLabel(page, totalPages)}</>}
            </DialogDescription>
          </div>

          {state.kind === 'image' && (
            <div className='flex items-center gap-1'>
              <Button
                type='button'
                size='sm'
                variant='outline'
                className='h-8 w-8 p-0'
                aria-label='Zoom out'
                disabled={!canZoomOut(zoom)}
                onClick={() => setZoom(zoomOut)}
              >
                <ZoomOut className='h-4 w-4' />
              </Button>
              <span className='w-12 text-center text-xs tabular-nums text-muted-foreground'>
                {Math.round(zoom * 100)}%
              </span>
              <Button
                type='button'
                size='sm'
                variant='outline'
                className='h-8 w-8 p-0'
                aria-label='Zoom in'
                disabled={!canZoomIn(zoom)}
                onClick={() => setZoom(zoomIn)}
              >
                <ZoomIn className='h-4 w-4' />
              </Button>
            </div>
          )}

          {state.kind === 'pdf' && hasPageNavigation(totalPages) && (
            <div className='flex items-center gap-1'>
              <Button
                type='button'
                size='sm'
                variant='outline'
                className='h-8 w-8 p-0'
                aria-label='Previous page'
                disabled={page <= 1}
                onClick={() => setPage((p) => clampPage(p - 1, totalPages))}
              >
                <ChevronLeft className='h-4 w-4' />
              </Button>
              <input
                type='number'
                aria-label='Page number'
                min={1}
                max={totalPages}
                value={page}
                onChange={(e) => setPage(clampPage(Number(e.target.value), totalPages))}
                className='h-8 w-14 rounded-md border bg-background px-2 text-center text-xs tabular-nums'
              />
              <Button
                type='button'
                size='sm'
                variant='outline'
                className='h-8 w-8 p-0'
                aria-label='Next page'
                disabled={page >= totalPages}
                onClick={() => setPage((p) => clampPage(p + 1, totalPages))}
              >
                <ChevronRight className='h-4 w-4' />
              </Button>
            </div>
          )}

          <Button type='button' size='sm' variant='outline' className='h-8 gap-1.5' onClick={download}>
            <Download className='h-4 w-4' />
            Download
          </Button>
        </div>

        <div className='min-h-0 flex-1 overflow-auto bg-muted/30 p-4'>
          {state.kind === 'loading' && (
            <div className='flex h-full items-center justify-center'>
              <Loader2 className='h-5 w-5 animate-spin text-muted-foreground' />
            </div>
          )}

          {state.kind === 'unavailable' && (
            <div className='flex h-full flex-col items-center justify-center gap-2 text-center'>
              <FileWarning className='h-6 w-6 text-muted-foreground' />
              <p className='max-w-sm text-sm text-muted-foreground'>
                {PREVIEW_UNAVAILABLE_MESSAGE}
              </p>
            </div>
          )}

          {state.kind === 'image' && (
            <div className='flex min-h-full items-center justify-center'>
              <img
                src={state.url}
                alt={fileName}
                style={{ width: `${zoom * 100}%` }}
                className='max-w-none object-contain'
                // A supported extension whose bytes are unreadable lands here.
                onError={() => setState({ kind: 'unavailable' })}
              />
            </div>
          )}

          {state.kind === 'pdf' && (
            <PdfPage
              url={state.url}
              page={page}
              onLoaded={setTotalPages}
              onFailed={() => setState({ kind: 'unavailable' })}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

/**
 * One rendered PDF page on a canvas.
 *
 * The document is loaded once and kept for the life of the panel — re-opening
 * it per page would re-download the file on every click. `pdfjs-dist` is pulled
 * in with a dynamic import so it is a separate chunk, fetched the first time
 * someone opens a PDF and never before.
 */
function PdfPage({
  url,
  page,
  onLoaded,
  onFailed,
}: {
  url: string
  page: number
  onLoaded: (totalPages: number) => void
  onFailed: () => void
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  // Typed loosely on purpose: pdfjs' own types are not exported in a shape
  // worth threading through here, and nothing outside this component sees it.
  const docRef = useRef<{ numPages: number; getPage: (n: number) => Promise<unknown> } | null>(null)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    let cancelled = false
    setReady(false)
    docRef.current = null

    void (async () => {
      try {
        const pdfjs = await import('pdfjs-dist')
        // The worker has to be addressed as a URL Vite can fingerprint; the
        // default resolution looks for a sibling file that is not there after a
        // build, and the render then hangs rather than failing loudly.
        const workerUrl = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default
        pdfjs.GlobalWorkerOptions.workerSrc = workerUrl

        const doc = await pdfjs.getDocument({ url }).promise
        if (cancelled) return
        docRef.current = doc as unknown as typeof docRef.current
        onLoaded(doc.numPages)
        setReady(true)
      } catch {
        // A truncated or zero-byte file fails here, and says what an
        // unsupported type says — see the note on the panel.
        if (!cancelled) onFailed()
      }
    })()

    return () => {
      cancelled = true
    }
  }, [url, onLoaded, onFailed])

  useEffect(() => {
    if (!ready) return
    const doc = docRef.current
    const canvas = canvasRef.current
    if (!doc || !canvas) return

    let cancelled = false
    void (async () => {
      try {
        const pdfPage = (await doc.getPage(clampPage(page, doc.numPages))) as {
          getViewport: (o: { scale: number }) => { width: number; height: number }
          render: (o: unknown) => { promise: Promise<void>; cancel: () => void }
        }
        if (cancelled) return
        const viewport = pdfPage.getViewport({ scale: 1.5 })
        const context = canvas.getContext('2d')
        if (!context) return
        canvas.width = viewport.width
        canvas.height = viewport.height
        await pdfPage.render({ canvas, canvasContext: context, viewport }).promise
      } catch {
        if (!cancelled) onFailed()
      }
    })()

    return () => {
      cancelled = true
    }
  }, [ready, page, onFailed])

  return (
    <div className='flex min-h-full items-start justify-center'>
      {!ready && <Loader2 className='mt-8 h-5 w-5 animate-spin text-muted-foreground' />}
      <canvas ref={canvasRef} className={ready ? 'max-w-full shadow-sm' : 'hidden'} />
    </div>
  )
}
